/**
 * MagicLinkButton · v2.7.65+E71
 * ══════════════════════════════════════════════════════════════════════
 * Small "Sign in with a magic link" launcher used on the Driver,
 * Dispatch, and Ride login panels + the standalone /admin/login page.
 *
 * Click → opens a popover with an email input → POST
 * /api/auth/magic-link/request with {email, portal}. Backend returns a
 * generic success message either way (anti-enumeration).
 */
import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, Loader2 } from "lucide-react";

export default function MagicLinkButton({ portal = "driver", defaultEmail = "", testid = "magic-link-btn" }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(defaultEmail);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (!email.trim()) {
      toast.error("Enter your email first");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/auth/magic-link/request", { email: email.trim(), portal });
      // Backend returns identical success payload whether or not the
      // account exists. UI reflects that: we always say "sent".
      const msg = r?.data?.message || "If an account with that email exists, a sign-in link has been sent.";
      toast.success(msg, { duration: 6000 });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send sign-in link");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <Button
        type="button"
        onClick={() => setOpen(true)}
        variant="outline"
        className="w-full border-zinc-700 hover:bg-zinc-800 h-10 text-xs"
        data-testid={testid}
      >
        <Mail className="h-3.5 w-3.5 mr-2"/>
        Email me a sign-in link
      </Button>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-2 rounded-md border border-zinc-800 bg-zinc-950/60 p-3" data-testid={`${testid}-form`}>
      <div className="flex items-center gap-2">
        <Mail className="h-3.5 w-3.5 text-amber-400"/>
        <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-200">Magic-link sign-in</span>
      </div>
      {sent ? (
        <div className="text-[11px] text-emerald-300" data-testid={`${testid}-sent-copy`}>
          If an account for <span className="font-mono text-emerald-200">{email}</span> exists, we&apos;ve sent a sign-in link. Check your inbox — the link expires in 15 minutes.
        </div>
      ) : (
        <>
          <Input
            data-testid={`${testid}-email-input`}
            type="email"
            autoFocus
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="bg-zinc-950 border-zinc-700 h-9 text-xs"
          />
          <div className="flex items-center gap-2">
            <Button
              type="submit"
              disabled={busy}
              className="flex-1 bg-amber-400 hover:bg-amber-300 text-zinc-950 h-8 text-xs font-bold"
              data-testid={`${testid}-submit`}
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin"/> : "Send Link"}
            </Button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-zinc-300"
              data-testid={`${testid}-cancel`}
            >
              cancel
            </button>
          </div>
        </>
      )}
    </form>
  );
}
